import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  useMermas,
  useMotivos,
  useSugerencias,
  useCreateMerma,
  useCreateMermasMultiples,
  useAprobarMerma,
  useRechazarMerma,
  useDeleteMerma,
} from '../hooks/useMermas';
import { useLotes } from '../hooks/useLotes';
import PageHeader from '../components/PageHeader';
import { formatearFechaCorta } from '../utils/dateUtils';
import {
  Trash2,
  Plus,
  AlertTriangle,
  Eye,
  X,
  CheckCircle,
  XCircle,
  Filter,
} from 'lucide-react';
import type {
  Merma,
  SugerenciaMerma,
  FiltrosMerma,
} from '../types/mermas';
import {
  getEstadoColor,
  getEstadoLabel,
  getPrioridadColor,
  getPrioridadLabel,
} from '../types/mermas';

const Mermas: React.FC = () => {
  const { user } = useAuth();
  const [filtros, setFiltros] = useState<FiltrosMerma>({
    page: 1,
    limit: 20,
  });
  const [showFormulario, setShowFormulario] = useState(false);
  const [showSugerencias, setShowSugerencias] = useState(true);
  const [showFiltros, setShowFiltros] = useState(false);
  const [mermaSeleccionada, setMermaSeleccionada] = useState<Merma | null>(
    null
  );

  // Hooks de datos
  const { data: mermasData, isLoading: loadingMermas } = useMermas(filtros);
  const { data: motivosData } = useMotivos();
  const { data: sugerenciasData, isLoading: loadingSugerencias } =
    useSugerencias({
      diasAnticipacion: 7,
    });
  const { data: lotesData } = useLotes({ page: 1, limit: 100 });

  // Mutations
  const createMerma = useCreateMerma();
  const createMermasMultiples = useCreateMermasMultiples();
  const aprobarMerma = useAprobarMerma();
  const rechazarMerma = useRechazarMerma();
  const deleteMerma = useDeleteMerma();

  const mermas = mermasData?.data || [];
  const motivos = motivosData || [];
  const sugerencias = sugerenciasData || [];
  const lotes = lotesData?.lotes || [];
  const pagination = mermasData?.pagination;

  const esAdmin = user?.rol === 'administrador';
  const puedeGestionarMermas = [
    'administrador',
    'chef',
    'personal_cocina',
  ].includes(user?.rol || '');

  // Estado del formulario
  const [formulario, setFormulario] = useState<{
    items: Array<{
      id: string;
      lote_id: number;
      ingrediente_id: number;
      cantidad: number;
      motivo: string;
      observaciones?: string;
    }>;
    observaciones_generales?: string;
  }>({
    items: [],
  });

  const handleAgregarSugerencia = (sugerencia: SugerenciaMerma) => {
    const nuevoItem = {
      id: `temp-${Date.now()}`,
      lote_id: sugerencia.lote_id,
      ingrediente_id: sugerencia.ingrediente_id,
      cantidad: sugerencia.cantidad_disponible,
      motivo: sugerencia.motivo_sugerido,
      observaciones: `Sugerencia automática: ${sugerencia.motivo_sugerido} - ${sugerencia.dias_para_vencimiento} días para vencimiento`,
    };

    setFormulario((prev) => ({
      ...prev,
      items: [...prev.items, nuevoItem],
    }));

    setShowFormulario(true);
  };

  const handleAgregarManual = () => {
    const nuevoItem = {
      id: `temp-${Date.now()}`,
      lote_id: 0,
      ingrediente_id: 0,
      cantidad: 0,
      motivo: '',
      observaciones: '',
    };

    setFormulario((prev) => ({
      ...prev,
      items: [...prev.items, nuevoItem],
    }));
  };

  const handleEliminarItem = (itemId: string) => {
    setFormulario((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== itemId),
    }));
  };

  const handleActualizarItem = (itemId: string, campo: string, valor: any) => {
    setFormulario((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === itemId ? { ...item, [campo]: valor } : item
      ),
    }));
  };

  const handleGuardarMermas = async () => {
    if (formulario.items.length === 0) {
      alert('Debe agregar al menos un item');
      return;
    }

    // Validar items
    const itemsInvalidos = formulario.items.filter(
      (item) =>
        !item.lote_id || !item.ingrediente_id || !item.cantidad || !item.motivo
    );

    if (itemsInvalidos.length > 0) {
      alert('Todos los campos son obligatorios');
      return;
    }

    // Fix tanda3 H6 (2026-07-01): validación pre-envío. Si cantidad
    // supera el stock del lote, el backend rechaza con 400
    // "Cantidad insuficiente en lote" (y hasta hace unos días el
    // mensaje se perdía por parseo mal en useCreateMerma). Ahora
    // avisamos con detalle ANTES de disparar la request para que el
    // chef entienda por qué no se puede.
    const lotesArr = ((lotesData?.lotes ?? []) as unknown) as Array<{
      id: number;
      cantidad_disponible?: number | string;
      unidad_medida?: string;
    }>;
    const lotesById = new Map(lotesArr.map((l) => [l.id, l]));
    const excedidos = formulario.items
      .map((item, idx) => {
        const lote = lotesById.get(item.lote_id);
        if (!lote) return null;
        const disponible = Number(lote.cantidad_disponible ?? 0);
        if (item.cantidad > disponible) {
          return {
            idx: idx + 1,
            disponible,
            solicitado: item.cantidad,
            unidad: lote.unidad_medida ?? '',
          };
        }
        return null;
      })
      .filter((x): x is { idx: number; disponible: number; solicitado: number; unidad: string } => x !== null);
    if (excedidos.length > 0) {
      const detalle = excedidos
        .map((e) => `Item #${e.idx}: solicitaste ${e.solicitado} ${e.unidad}, disponible ${e.disponible} ${e.unidad}`)
        .join('\n');
      alert(
        `No se puede registrar la merma: la cantidad supera el stock disponible.\n\n${detalle}`
      );
      return;
    }

    try {
      if (formulario.items.length === 1) {
        const item = formulario.items[0];
        await createMerma.mutateAsync({
          lote_id: item.lote_id,
          ingrediente_id: item.ingrediente_id,
          cantidad: item.cantidad,
          motivo: item.motivo,
          observaciones: item.observaciones,
        });
      } else {
        await createMermasMultiples.mutateAsync({
          items: formulario.items.map((item) => ({
            lote_id: item.lote_id,
            ingrediente_id: item.ingrediente_id,
            cantidad: item.cantidad,
            motivo: item.motivo,
            observaciones: item.observaciones,
          })),
          observaciones_generales: formulario.observaciones_generales,
        });
      }

      // Limpiar formulario
      setFormulario({ items: [] });
      setShowFormulario(false);
    } catch (error) {
      console.error('Error al guardar mermas:', error);
    }
  };

  const handleAprobar = async (merma: Merma) => {
    if (!esAdmin) return;

    const comentario = prompt('Comentario de aprobación (opcional):');

    try {
      await aprobarMerma.mutateAsync({
        id: merma.id,
        payload: { comentario: comentario || undefined },
      });
    } catch (error) {
      console.error('Error al aprobar merma:', error);
    }
  };

  const handleRechazar = async (merma: Merma) => {
    if (!esAdmin) return;

    const comentario = prompt('Comentario de rechazo (requerido):');

    if (!comentario) {
      alert('El comentario es requerido para rechazar');
      return;
    }

    try {
      await rechazarMerma.mutateAsync({
        id: merma.id,
        payload: { comentario },
      });
    } catch (error) {
      console.error('Error al rechazar merma:', error);
    }
  };

  const handleEliminar = async (merma: Merma) => {
    if (!confirm('¿Está seguro de eliminar esta merma?')) return;

    try {
      await deleteMerma.mutateAsync(merma.id);
    } catch (error) {
      console.error('Error al eliminar merma:', error);
    }
  };

  const formatDate = (dateString: string) => formatearFechaCorta(dateString);

  if (loadingMermas) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <PageHeader title="Mermas" helpSeccion="mermas" />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader
        title="Mermas"
        helpSeccion="mermas"
        actions={
          puedeGestionarMermas ? (
            <div className="flex gap-2">
              <button
                onClick={() => setShowFiltros(!showFiltros)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filtros
              </button>
              <button
                onClick={() => setShowFormulario(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Nueva Merma
              </button>
            </div>
          ) : null
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filtros */}
        {showFiltros && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              Filtros
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estado
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  value={filtros.estado || ''}
                  onChange={(e) =>
                    setFiltros({
                      ...filtros,
                      estado: e.target.value as any,
                    })
                  }
                >
                  <option value="">Todos</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="aprobado">Aprobado</option>
                  <option value="rechazado">Rechazado</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha Desde
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  value={filtros.fechaDesde || ''}
                  onChange={(e) =>
                    setFiltros({ ...filtros, fechaDesde: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha Hasta
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  value={filtros.fechaHasta || ''}
                  onChange={(e) =>
                    setFiltros({ ...filtros, fechaHasta: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setFiltros({ page: 1, limit: 20 })}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Limpiar
              </button>
            </div>
          </div>
        )}

        {/* Sugerencias */}
        {showSugerencias && sugerencias.length > 0 && puedeGestionarMermas && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="w-6 h-6 text-orange-500" />
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Sugerencias de Merma
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {sugerencias.length} lote(s) próximos a vencer o con
                      problemas
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSugerencias(false)}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {loadingSugerencias ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sugerencias.map((sugerencia) => (
                    <div
                      key={`${sugerencia.lote_id}-${sugerencia.ingrediente_id}`}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {sugerencia.ingrediente_nombre}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Lote: {sugerencia.numero_lote}
                          </p>
                        </div>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getPrioridadColor(
                            sugerencia.prioridad
                          )}`}
                        >
                          {getPrioridadLabel(sugerencia.prioridad)}
                        </span>
                      </div>

                      <div className="space-y-2 text-sm mb-4">
                        {/* Fix tanda3 H5 (2026-07-01): mostrar EL LOTE
                            (afectado por esta sugerencia) y ALSO el
                            stock TOTAL del ingrediente — antes solo se
                            veía el lote (600g) y confundía porque el
                            ingrediente en total podía tener 300g. */}
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">
                            En este lote:
                          </span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {sugerencia.cantidad_disponible}{' '}
                            {sugerencia.unidad_medida}
                          </span>
                        </div>
                        {(sugerencia as unknown as { stock_ingrediente?: number | string }).stock_ingrediente !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">
                              Stock ingrediente:
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {(sugerencia as unknown as { stock_ingrediente?: number | string }).stock_ingrediente}{' '}
                              {sugerencia.unidad_medida}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">
                            Vencimiento:
                          </span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatDate(sugerencia.fecha_vencimiento)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">
                            Días restantes:
                          </span>
                          <span className="font-medium text-red-600 dark:text-red-400">
                            {sugerencia.dias_para_vencimiento}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>Motivo:</strong> {sugerencia.motivo_sugerido}
                        </div>
                      </div>

                      <button
                        onClick={() => handleAgregarSugerencia(sugerencia)}
                        className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                      >
                        Agregar a Merma
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Listado de Mermas */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Registro de Mermas
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total: {pagination?.total || 0} mermas
                </p>
              </div>
              {pagination && pagination.totalPages > 1 && (
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setFiltros({ ...filtros, page: (filtros.page || 1) - 1 })
                    }
                    disabled={(filtros.page || 1) <= 1}
                    className="px-3 py-1 bg-gray-500 text-white rounded disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span className="px-3 py-1 text-gray-900 dark:text-white">
                    Página {filtros.page || 1} de {pagination.totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setFiltros({ ...filtros, page: (filtros.page || 1) + 1 })
                    }
                    disabled={(filtros.page || 1) >= pagination.totalPages}
                    className="px-3 py-1 bg-gray-500 text-white rounded disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ingrediente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Lote
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Cantidad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Motivo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Usuario
                  </th>
                  {esAdmin && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {mermas.length === 0 ? (
                  <tr>
                    <td
                      colSpan={esAdmin ? 8 : 7}
                      className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
                    >
                      No hay mermas registradas
                    </td>
                  </tr>
                ) : (
                  mermas.map((merma) => (
                    <tr
                      key={merma.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(merma.fecha_registro)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {merma.ingrediente_nombre}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {merma.numero_lote}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {merma.cantidad} {merma.unidad_medida}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                        {merma.motivo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getEstadoColor(
                            merma.estado
                          )}`}
                        >
                          {getEstadoLabel(merma.estado)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {merma.usuario_nombre}
                      </td>
                      {esAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            {merma.estado === 'pendiente' && (
                              <>
                                <button
                                  onClick={() => handleAprobar(merma)}
                                  className="text-green-600 hover:text-green-900 dark:hover:text-green-400"
                                  title="Aprobar"
                                >
                                  <CheckCircle className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleRechazar(merma)}
                                  className="text-red-600 hover:text-red-900 dark:hover:text-red-400"
                                  title="Rechazar"
                                >
                                  <XCircle className="w-5 h-5" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => setMermaSeleccionada(merma)}
                              className="text-blue-600 hover:text-blue-900 dark:hover:text-blue-400"
                              title="Ver detalle"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleEliminar(merma)}
                              className="text-red-600 hover:text-red-900 dark:hover:text-red-400"
                              title="Eliminar"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Formulario */}
      {showFormulario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Registrar Mermas
                </h2>
                <button
                  onClick={() => setShowFormulario(false)}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Items */}
              <div className="space-y-4 mb-6">
                {formulario.items.map((item, index) => (
                  <div
                    key={item.id}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        Item #{index + 1}
                      </h3>
                      <button
                        onClick={() => handleEliminarItem(item.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Lote *
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={item.lote_id}
                          onChange={(e) => {
                            const lote = lotes.find(
                              (l: any) => l.id === Number(e.target.value)
                            );
                            handleActualizarItem(
                              item.id,
                              'lote_id',
                              Number(e.target.value)
                            );
                            if (lote) {
                              handleActualizarItem(
                                item.id,
                                'ingrediente_id',
                                lote.ingrediente_id
                              );
                            }
                          }}
                        >
                          <option value={0}>Seleccionar lote</option>
                          {lotes.map((lote: any) => (
                            <option key={lote.id} value={lote.id}>
                              {lote.numero_lote} - {lote.ingrediente_nombre} (
                              {lote.cantidad_disponible} {lote.unidad_medida})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Cantidad *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={item.cantidad}
                          onChange={(e) =>
                            handleActualizarItem(
                              item.id,
                              'cantidad',
                              Number(e.target.value)
                            )
                          }
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Motivo *
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={item.motivo}
                          onChange={(e) =>
                            handleActualizarItem(
                              item.id,
                              'motivo',
                              e.target.value
                            )
                          }
                        >
                          <option value="">Seleccionar motivo</option>
                          {motivos.map((motivo) => (
                            <option key={motivo.id} value={motivo.nombre}>
                              {motivo.nombre}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Observaciones
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          value={item.observaciones}
                          onChange={(e) =>
                            handleActualizarItem(
                              item.id,
                              'observaciones',
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleAgregarManual}
                className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Agregar Item
              </button>

              {/* Observaciones generales */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Observaciones Generales
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={3}
                  value={formulario.observaciones_generales || ''}
                  onChange={(e) =>
                    setFormulario({
                      ...formulario,
                      observaciones_generales: e.target.value,
                    })
                  }
                />
              </div>

              {/* Acciones */}
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => setShowFormulario(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleGuardarMermas}
                  disabled={
                    createMerma.isPending || createMermasMultiples.isPending
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createMerma.isPending || createMermasMultiples.isPending
                    ? 'Guardando...'
                    : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle */}
      {mermaSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Detalle de Merma #{mermaSeleccionada.id}
                </h2>
                <button
                  onClick={() => setMermaSeleccionada(null)}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Ingrediente
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {mermaSeleccionada.ingrediente_nombre}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Lote
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {mermaSeleccionada.numero_lote}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Cantidad
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {mermaSeleccionada.cantidad}{' '}
                    {mermaSeleccionada.unidad_medida}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Costo Total
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    ${mermaSeleccionada.costo_total.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Motivo
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {mermaSeleccionada.motivo}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Estado
                  </p>
                  <span
                    className={`inline-block px-2 py-1 rounded text-xs font-medium ${getEstadoColor(
                      mermaSeleccionada.estado
                    )}`}
                  >
                    {getEstadoLabel(mermaSeleccionada.estado)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Fecha Registro
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {formatDate(mermaSeleccionada.fecha_registro)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Usuario
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {mermaSeleccionada.usuario_nombre}
                  </p>
                </div>
              </div>

              {mermaSeleccionada.observaciones && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Observaciones
                  </p>
                  <p className="text-gray-900 dark:text-white">
                    {mermaSeleccionada.observaciones}
                  </p>
                </div>
              )}

              {mermaSeleccionada.aprobado_por && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Aprobado por
                      </p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {mermaSeleccionada.aprobador_nombre}
                      </p>
                    </div>
                    {mermaSeleccionada.fecha_aprobacion && (
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Fecha Aprobación
                        </p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {formatDate(mermaSeleccionada.fecha_aprobacion)}
                        </p>
                      </div>
                    )}
                  </div>
                  {mermaSeleccionada.comentario_aprobacion && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Comentario
                      </p>
                      <p className="text-gray-900 dark:text-white">
                        {mermaSeleccionada.comentario_aprobacion}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setMermaSeleccionada(null)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mermas;
